import { FC, MutableRefObject, PropsWithChildren, useCallback, useMemo, useRef } from 'react';

import chassisTopSVG from 'assets/svgs/chassis-top-view.svg';
import {
  ChassisDamageLocation,
  ChassisDamageTypes,
  DamageType,
  EquipmentType,
  chassisDamageHasLocation,
  chassisDamageLocationOptions,
  chassisDamageWithLocationHasPart,
  isChassisDamage
} from 'types/damage';

import { useDamageOptions } from 'contexts/damage-options';
import Webcam from 'react-webcam';

import { CAPTURE_VIDEO_CONSTRAINTS } from '../capture-constraints';
import { ImageOverlay } from 'utils';
import { useDamage } from '.';
import { useNewTransaction } from '../new-transaction.context';

const isChassisDamageLocation = (key: string): key is ChassisDamageLocation =>
  Object.values(ChassisDamageLocation).some(value => key === value);

const ChassisDialogWrapper: FC<
  PropsWithChildren<{ className?: string; back?: () => void; next?: () => void }>
> = props => {
  return (
    <div>
      <div className="d-flex bg-primary g-3 p-1">
        <button type="button" className="btn-back d-flex g-2" onClick={props.back} disabled={!props.back}>
          <span className="material-icons icon-large">arrow_back</span>
          <span>BACK</span>
        </button>
        <h4 className="flex-grow-1 text-center">CHASSIS DAMAGE SELECTION</h4>
        <button type="button" className="btn-back d-flex g-2" onClick={props.next} disabled={!props.next}>
          <span>NEXT</span>
          <span className="material-icons icon-large">arrow_forward</span>
        </button>
      </div>
      <div className={`dialog-content ${props.className ?? ''} p-5`}>{props.children}</div>
    </div>
  );
};

const DamageImage: FC<{ closeDialog: () => void }> = () => {
  const { current, capture, setCapture, setImage } = useDamage();
  const { container, chassis, genset, stack, bulk } = useNewTransaction();
  const webcamRef = useRef<Webcam>(null);

  const handleCapture = useCallback(async () => {
    const text = bulk
      ? (chassis.unitNumber ?? '') +
        ' ' +
        stack
          .map(u => u.unitNumber ?? '')
          .slice(0, 3)
          .join(' ')
      : [container.unitNumber ?? '', chassis.unitNumber ?? ' ', genset.unitNumber ?? ' '].join(' ');
    ImageOverlay.withText(text + ' ' + new Date().toLocaleString())
      .withSetter(setImage)
      .call(Webcam.prototype.getScreenshot.call(webcamRef.current));
  }, [setImage, bulk, stack, container.unitNumber, chassis.unitNumber, genset.unitNumber]);

  return !capture ? null : (
    <ChassisDialogWrapper
      className="webcam"
      back={() => setCapture(false)}
      next={!current.imageBase64 ? undefined : () => setCapture(false)}
    >
      {!current.imageBase64 ? (
        <div className="d-flex flex-column">
          <Webcam
            width="100%"
            ref={webcamRef}
            videoConstraints={CAPTURE_VIDEO_CONSTRAINTS}
            screenshotFormat="image/jpeg"
          />
          <button type="button" className="btn-close" onClick={handleCapture}>
            <span className="material-icons">circle</span>
          </button>
        </div>
      ) : (
        <div className="d-flex flex-column">
          <img alt="preview" width="100%" src={current.imageBase64} />
          <button type="button" className="btn-close" onClick={() => setImage(undefined)}>
            <span className="material-icons">rotate_left</span>
          </button>
        </div>
      )}
    </ChassisDialogWrapper>
  );
};

const DamageTypeSelection: FC<{ closeDialog: () => void }> = ({ closeDialog }) => {
  const { current, setCapture, setCurrent, setIndex, submit } = useDamage();
  const { chassisDamageMap } = useDamageOptions();

  const damageLocationRef: MutableRefObject<HTMLSelectElement | null> = useRef(null);
  const damageTypeRef: MutableRefObject<HTMLSelectElement | null> = useRef(null);

  const handleSubmit = () => {
    if (
      isChassisDamage(current) &&
      !current.subLocation &&
      chassisDamageLocationOptions[current.location as ChassisDamageLocation][current.part as ChassisPart]?.locations
        ?.length
    ) {
      damageLocationRef.current?.setCustomValidity('Damage location required');
      damageLocationRef.current?.reportValidity();
    } else if (!current.damageType) {
      damageTypeRef.current?.setCustomValidity('Damage type required');
      damageTypeRef.current?.reportValidity();
    } else {
      damageLocationRef.current?.setCustomValidity('');
      damageTypeRef.current?.setCustomValidity('');
      submit();
      closeDialog();
    }
  };

  return !isChassisDamage(current) ||
    !chassisDamageHasLocation(current) ||
    !chassisDamageWithLocationHasPart(current) ? null : (
    <ChassisDialogWrapper back={() => setIndex(1)} className="damage-type">
      <h5 className="mb-5 text-center text-primary">
        CHASSIS | {current.location} | {current.part}
      </h5>
      <div className="grid-3 p-3 auto-rows">
        <div className="damage-input col-span-2">
          {chassisDamageLocationOptions[current.location][current.part]?.locations?.length ? (
            <select
              className="w-100"
              value={current.subLocation ?? ''}
              onChange={e => setCurrent(p => ({ ...p, subLocation: e.target.value }))}
              ref={node => {
                node?.toggleAttribute('blank', !current.subLocation); /* label transform */
                damageLocationRef.current = node;
              }}
            >
              <option value="" />
              {Object.assign<ChassisDamageLocation[], string[] | undefined>(
                [],
                chassisDamageLocationOptions[current.location][current.part]?.locations
              ).map(location => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
          ) : (
            <input value={current.location} />
          )}
          <label>DAMAGE LOCATION *</label>
        </div>
        <div className="row-span-2 h-100 w-100">
          {!current.imageBase64 ? (
            <button
              type="button"
              className="btn-outline justify-content-center h-100 w-100 grid-1 text-primary"
              onClick={() => setCapture(true)}
            >
              <span className="material-icons image">camera_alt</span>
              <span className="white-space-nowrap text-base">TAKE PHOTO</span>
            </button>
          ) : (
            <button
              type="button"
              className="btn-outline justify-content-center text-info-main h-100 w-100 grid-1"
              onClick={() => setCapture(true)}
            >
              <span className="material-icons image">image</span>
              <span className="white-space-nowrap text-base">VIEW PHOTO</span>
            </button>
          )}
        </div>
        <div className="damage-input col-span-2">
          <select
            className="w-100"
            value={current.damageType ?? ''}
            onChange={e => setCurrent(p => ({ ...p, damageType: (e.target.value as DamageType) || undefined }))}
            ref={node => {
              node?.toggleAttribute('blank', !current.damageType); /* label transform */
              damageTypeRef.current = node;
            }}
          >
            <option value="" />
            {Object.assign<DamageType[], DamageType[] | undefined>(
              [],
              chassisDamageMap[current.location][current.part]
            ).map(damageType => (
              <option key={damageType} value={damageType}>
                {damageType}
              </option>
            ))}
          </select>
          <label>DAMAGE TYPE *</label>
        </div>
        <div className="damage-input col-span-3 row-span-2 h-100">
          <textarea
            className="h-100 w-100"
            ref={node => node?.toggleAttribute('blank', !current.notes)}
            value={current.notes}
            onChange={e => setCurrent(p => ({ ...p, notes: e.target.value }))}
          />
          <label>CHASSIS REPAIR NOTES</label>
        </div>
        <button className="btn-outline col-start-2 align-self-end cancel" type="button" onClick={closeDialog}>
          CANCEL
        </button>
        <button className="btn-primary col-start-3 align-self-end" type="button" onClick={handleSubmit}>
          SUBMIT
        </button>
      </div>
    </ChassisDialogWrapper>
  );
};

type ChassisPart = keyof ChassisDamageTypes[keyof ChassisDamageTypes];
const DamagePartSelection: FC<{ closeDialog: () => void }> = () => {
  const { current, damages, setCurrent, setIndex } = useDamage();
  const { chassisDamageMap } = useDamageOptions();

  const counts = useMemo(() => {
    return damages
      .filter(isChassisDamage)
      .filter(chassisDamageHasLocation)
      .filter(chassisDamageWithLocationHasPart)
      .filter(damage => damage.location === current.location)
      .reduce((acc: { [key: string]: number }, { part }) => {
        return { ...acc, [part]: (acc[part] ?? 0) + 1 };
      }, {});
  }, [current.location, damages]);

  return !isChassisDamage(current) || !chassisDamageHasLocation(current) ? null : (
    <ChassisDialogWrapper
      back={() => setIndex(0)}
      next={chassisDamageWithLocationHasPart(current) ? () => setIndex(2) : undefined}
    >
      <h5 className="mb-5 text-center text-primary">CHASSIS | {current.location}</h5>
      <div className="grid-2">
        {Object.assign<ChassisPart[], any[]>([], Object.keys({ ...chassisDamageMap[current.location] })).map(part => (
          <div key={part} className="badge-container h-100 w-100">
            <button
              type="button"
              className="btn-outline justify-content-center text-info-main g-2 h-100 w-100"
              onClick={() => {
                setCurrent({ equipmentType: EquipmentType.CHASSIS, location: current.location, part });
                setIndex(2);
              }}
              ref={node => node?.toggleAttribute('selected', part === current.part)}
            >
              <label className="font-weight-bold text-big"> {part}</label>
            </button>
            {!!counts[part] && <div className="badge background-primary-dark text-md text-white">{counts[part]}</div>}
          </div>
        ))}
      </div>
    </ChassisDialogWrapper>
  );
};

const DamageLocationSelection: FC<{ closeDialog: () => void }> = ({ closeDialog }) => {
  const { current, damages, setCurrent, setIndex } = useDamage();
  const { chassisDamageMap } = useDamageOptions();

  const counts = useMemo(() => {
    return damages
      .filter(isChassisDamage)
      .filter(chassisDamageHasLocation)
      .reduce((acc: { [key: string]: number }, { location }) => {
        return { ...acc, [location]: (acc[location] ?? 0) + 1 };
      }, {});
  }, [damages]);

  return !isChassisDamage(current) ? null : (
    <ChassisDialogWrapper back={closeDialog} next={chassisDamageHasLocation(current) ? () => setIndex(1) : undefined}>
      <h5 className="text-center text-primary">CHASSIS OVERVIEW</h5>
      <img alt="chassis" className="p-5" src={chassisTopSVG} />
      <div className="grid-3">
        {Object.keys(chassisDamageMap)
          .filter(isChassisDamageLocation)
          .map(location => (
            <div key={location} className="badge-container h-100 w-100">
              <button
                type="button"
                className="btn-outline text-info-main g-2 h-100 w-100"
                onClick={() => {
                  setCurrent({ equipmentType: EquipmentType.CHASSIS, location });
                  setIndex(1);
                }}
                ref={node => node?.toggleAttribute('selected', location === current.location)}
              >
                <label className="font-weight-bold text-big">{location}</label>
              </button>
              {!!counts[location] && (
                <div className="badge background-primary-dark text-md text-white">{counts[location]}</div>
              )}
            </div>
          ))}
      </div>
    </ChassisDialogWrapper>
  );
};

const ChassisDialogContent: FC<{ closeDialog: () => void }> = ({ closeDialog }) => {
  const { capture, current, index } = useDamage();

  const Selection: FC<{ closeDialog: () => void }> = useMemo(() => {
    return !isChassisDamage(current)
      ? () => null
      : !chassisDamageHasLocation(current) || index === 0
      ? DamageLocationSelection
      : !chassisDamageWithLocationHasPart(current) || index === 1
      ? DamagePartSelection
      : !capture
      ? DamageTypeSelection
      : DamageImage;
  }, [capture, current, index]);

  return <Selection closeDialog={closeDialog} />;
};

export default ChassisDialogContent;
